import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { AdminInvite, AdminInviteDelivery, GrantSpec } from "@equipped/core/backend/client";
import { useAuth } from "../auth/AuthContext";
import { useCatalogTree } from "../lib/useCatalogTree";
import { publicContentBaseUrl } from "../lib/contentBase";
import { MainCard } from "../components/MainCard";
import { TabbedPage } from "../components/TabbedPage";
import { GrantPicker } from "../components/GrantPicker";

/** Mirrors `apps/admin-web/src/pages/InvitesPage.tsx`'s `inviteMeta` verbatim. */
function inviteMeta(i: AdminInvite): string {
  const parts: string[] = [];
  parts.push(i.active ? "active" : "inactive");
  if (i.maxUses != null) parts.push(`${i.usedCount}/${i.maxUses} used`);
  else parts.push(`${i.usedCount} used`);
  if (i.expiresAt) parts.push(`expires ${new Date(i.expiresAt * 1000).toLocaleDateString()}`);
  if (i.grants?.length) parts.push(`${i.grants.length} granted`);
  if (i.note) parts.push(`note: ${i.note}`);
  if (i.message) parts.push(`message: "${i.message}"`);
  return parts.join(" · ");
}

/** Mirrors `InvitesPage.tsx`'s `daysUntil` verbatim — remaining whole days until an absolute
 *  `expiresAt`, for pre-filling the edit form's "Days" input. */
function daysUntil(expiresAt: number | null): string {
  if (!expiresAt) return "";
  const remainingMs = expiresAt * 1000 - Date.now();
  if (remainingMs <= 0) return "";
  return String(Math.ceil(remainingMs / 86_400_000));
}

interface CreateFormState {
  code: string;
  maxUses: string;
  days: string;
  note: string;
  message: string;
  email: string;
  grants: GrantSpec[];
}

const EMPTY_FORM: CreateFormState = { code: "", maxUses: "", days: "", note: "", message: "", email: "", grants: [] };

function buildBody(form: CreateFormState) {
  const body: { code?: string; maxUses?: number; days?: number; grants?: GrantSpec[]; note?: string; message?: string } = {};
  if (form.code.trim()) body.code = form.code.trim();
  if (form.maxUses.trim()) body.maxUses = Number(form.maxUses);
  if (form.days.trim()) body.days = Number(form.days);
  if (form.grants.length) body.grants = form.grants;
  if (form.note.trim()) body.note = form.note.trim();
  if (form.message.trim()) body.message = form.message.trim();
  return body;
}

/**
 * Invites tab, re-skinned to MUI/Mantis (docs/337-ADMIN-MANTIS-MIGRATION.md v1) — the behavioral
 * reference is `specs/admin/screens/invites.md` / `apps/admin/app/(tabs)/invites.tsx`. Same
 * `useQuery`/`useMutation` shapes as `apps/admin-web/src/pages/InvitesPage.tsx`, lifted UNCHANGED
 * — create/mint/send/edit/revoke + per-code delivery status (docs/273), the `GrantPicker`
 * (`useCatalogTree` + `contentBase`), and a link to the Invite Acceptance History page (routed to
 * `ComingSoonPage` in this "half the work" build — see `App.tsx`).
 *
 * **Split into `TabbedPage` tabs** (mirrors `CatalogsPage`/`ProviderSettingsPage`'s own docs/365
 * `TabbedPage`/`useTabParam` adoption): the owner reported the page as "one long scroll" — the
 * create-invite form used to sit above the full invite list on the same scroll. **Invites**
 * (the list — default landing, deep-linkable as `?tab=invites`) and **Create invite** (the form)
 * are now separate tabs; every existing action (mint/send, edit note/message/grants, revoke/
 * reactivate, per-code deliveries + resend) is unchanged, just relocated. The Invites tab also
 * gains a client-side **search box** (`query`) filtering the already-loaded invite list by code/
 * note/message (invites carry no `email` field of their own — only per-code `AdminInviteDelivery`
 * rows, lazily fetched only once a row's own "Deliveries" panel is expanded — so email isn't a
 * searchable field here) — a plain controlled filter, no debounce, since it's over data already
 * in memory (mirrors `CatalogsPage`'s own Search tab, not `PeoplePage`'s server-driven debounce).
 */
export function InvitesPage() {
  const { client, token, backendUrl } = useAuth();
  const contentBaseUrl = publicContentBaseUrl(backendUrl);
  const queryClient = useQueryClient();
  const { tree, error: treeError } = useCatalogTree(backendUrl);

  const [form, setForm] = useState<CreateFormState>(EMPTY_FORM);

  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [editMaxUses, setEditMaxUses] = useState("");
  const [editDays, setEditDays] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editMessage, setEditMessage] = useState("");
  const [editGrants, setEditGrants] = useState<GrantSpec[]>([]);

  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<Record<string, AdminInviteDelivery[]>>({});
  const [resendEmail, setResendEmail] = useState<Record<string, string>>({});
  const [deliveriesBusy, setDeliveriesBusy] = useState(false);
  const [deliveriesError, setDeliveriesError] = useState<string | null>(null);

  const invitesQuery = useQuery({
    queryKey: ["admin-invites"],
    queryFn: () => client!.adminInvites(token!),
    enabled: !!client && !!token,
  });
  const invites = invitesQuery.data ?? [];

  // Client-side search over the already-loaded invite list (docs/365 item — the Invites
  // page has no cursor endpoint of its own, so a server-side query isn't applicable here,
  // unlike `PeoplePage`'s debounced `q=` param). Trimmed + case-insensitive; matches the
  // invite `code` and, when present, the admin-only `note`/user-facing `message` fields.
  const [query, setQuery] = useState("");
  const ql = query.trim().toLowerCase();
  const filteredInvites = ql
    ? invites.filter((inv) => `${inv.code} ${inv.note ?? ""} ${inv.message ?? ""}`.toLowerCase().includes(ql))
    : invites;

  const toggleGrant = (kind: "pack" | "catalog", ref: string) =>
    setForm((f) => {
      const exists = f.grants.some((g) => g.kind === kind && g.ref === ref);
      return { ...f, grants: exists ? f.grants.filter((g) => !(g.kind === kind && g.ref === ref)) : [...f.grants, { kind, ref }] };
    });
  const toggleEditGrant = (kind: "pack" | "catalog", ref: string) =>
    setEditGrants((list) => {
      const exists = list.some((g) => g.kind === kind && g.ref === ref);
      return exists ? list.filter((g) => !(g.kind === kind && g.ref === ref)) : [...list, { kind, ref }];
    });

  const upsertInvite = (inv: AdminInvite) =>
    queryClient.setQueryData<AdminInvite[]>(["admin-invites"], (list) => [inv, ...(list ?? []).filter((i) => i.code !== inv.code)]);
  const replaceInvite = (inv: AdminInvite) =>
    queryClient.setQueryData<AdminInvite[]>(["admin-invites"], (list) => (list ?? []).map((i) => (i.code === inv.code ? inv : i)));

  const createMutation = useMutation({
    mutationFn: () => client!.adminCreateInvite(token!, buildBody(form)),
    onSuccess: ({ invite }) => {
      upsertInvite(invite);
      setForm(EMPTY_FORM);
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => client!.adminCreateInvite(token!, { ...buildBody(form), email: form.email, send: true }),
    onSuccess: ({ invite, deliveries: sent }) => {
      upsertInvite(invite);
      if (sent?.length) {
        setDeliveries((d) => ({ ...d, [invite.code]: sent }));
        setExpandedCode(invite.code);
      }
      setForm(EMPTY_FORM);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (inv: AdminInvite) => client!.adminRevokeInvite(token!, inv.code, !inv.active),
    onSuccess: replaceInvite,
  });

  const saveEditMutation = useMutation({
    mutationFn: (inv: AdminInvite) =>
      client!.adminUpdateInvite(token!, inv.code, {
        maxUses: editMaxUses.trim() ? Number(editMaxUses) : null,
        days: editDays.trim() ? Number(editDays) : null,
        grants: editGrants,
        note: editNote.trim() ? editNote.trim() : null,
        message: editMessage.trim() ? editMessage.trim() : null,
      }),
    onSuccess: (inv) => {
      replaceInvite(inv);
      setEditingCode(null);
    },
  });

  const startEdit = (inv: AdminInvite) => {
    setEditingCode(inv.code);
    setEditMaxUses(inv.maxUses != null ? String(inv.maxUses) : "");
    setEditDays(daysUntil(inv.expiresAt));
    setEditNote(inv.note ?? "");
    setEditMessage(inv.message ?? "");
    setEditGrants(inv.grants ?? []);
  };

  const toggleDeliveries = (inv: AdminInvite) => {
    if (expandedCode === inv.code) {
      setExpandedCode(null);
      return;
    }
    setExpandedCode(inv.code);
    if (!deliveries[inv.code]) {
      setDeliveriesBusy(true);
      setDeliveriesError(null);
      client!
        .adminInviteDeliveries(token!, inv.code)
        .then((rows) => setDeliveries((d) => ({ ...d, [inv.code]: rows })))
        .catch((e) => setDeliveriesError((e as Error).message))
        .finally(() => setDeliveriesBusy(false));
    }
  };

  const resend = (inv: AdminInvite) => {
    const addr = (resendEmail[inv.code] ?? "").trim();
    if (!addr) return;
    setDeliveriesBusy(true);
    setDeliveriesError(null);
    client!
      .adminSendInvite(token!, inv.code, addr)
      .then((rows) => {
        setDeliveries((d) => ({ ...d, [inv.code]: [...rows, ...(d[inv.code] ?? [])] }));
        setResendEmail((m) => ({ ...m, [inv.code]: "" }));
      })
      .catch((e) => setDeliveriesError((e as Error).message))
      .finally(() => setDeliveriesBusy(false));
  };

  const createError = createMutation.isError ? (createMutation.error as Error).message : null;
  const sendError = sendMutation.isError ? (sendMutation.error as Error).message : null;

  const createTabContent = (
    <MainCard title="Create an invite">
      <Stack spacing={1.5}>
          <TextField
            size="small"
            placeholder="Code (blank = auto)"
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
          />
          <Stack direction="row" spacing={1.5}>
            <TextField
              size="small"
              type="number"
              placeholder="Max uses"
              value={form.maxUses}
              onChange={(e) => setForm((f) => ({ ...f, maxUses: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              type="number"
              placeholder="Days until expiry"
              value={form.days}
              onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))}
              fullWidth
            />
          </Stack>
          <TextField
            size="small"
            placeholder="Note (admin only — never shown to the invitee)"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
          />
          <TextField
            size="small"
            placeholder="Welcome message (shown to the invitee after they enter this code)"
            value={form.message}
            onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          />
          <GrantPicker tree={tree} baseUrl={contentBaseUrl} selected={form.grants} onToggle={toggleGrant} error={treeError} />
          <TextField
            size="small"
            type="email"
            placeholder="Email address(es) — comma or space separated"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          {createError ? <Alert severity="error">{createError}</Alert> : null}
          {sendError ? <Alert severity="error">{sendError}</Alert> : null}
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" disabled={createMutation.isPending} onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? <CircularProgress size={18} color="inherit" /> : "Create"}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              disabled={sendMutation.isPending || !form.email.trim()}
              onClick={() => sendMutation.mutate()}
            >
              {sendMutation.isPending ? <CircularProgress size={18} color="inherit" /> : "Send invite"}
            </Button>
          </Stack>
        </Stack>
      </MainCard>
  );

  const invitesTabContent = (
    <MainCard title="Invites">
      <TextField
        size="small"
        fullWidth
        placeholder="Search by code, note, or message…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 2 }}
      />

      {invitesQuery.isError ? <Alert severity="error">{(invitesQuery.error as Error).message}</Alert> : null}
      {invitesQuery.isLoading ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : invites.length === 0 ? (
        <Typography color="text.secondary">No invites yet.</Typography>
      ) : filteredInvites.length === 0 ? (
        <Typography color="text.secondary">No invites match your search.</Typography>
      ) : (
        <>
          {ql ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {filteredInvites.length} of {invites.length}
            </Typography>
          ) : null}
          <Stack spacing={1.5}>
          {filteredInvites.map((inv) => {
            // docs/340-ADMIN-MANTIS-AND-API-IDEAS.md §3 — `toggleActiveMutation` is ONE
            // shared mutation for every invite row, so `.isPending` alone used to disable
            // the Revoke/Reactivate button on EVERY row while one code was being toggled.
            // Scope to THIS row via the pending call's own `variables`.
            const togglePending = toggleActiveMutation.isPending && toggleActiveMutation.variables?.code === inv.code;
            return (
            <MainCard key={inv.code}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontFamily: "monospace" }}>{inv.code}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {inviteMeta(inv)}
                  </Typography>
                </Box>
                {editingCode !== inv.code ? (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Button size="small" variant="outlined" color="secondary" onClick={() => toggleDeliveries(inv)}>
                      {expandedCode === inv.code ? "Hide deliveries" : "Deliveries"}
                    </Button>
                    <Button size="small" variant="outlined" color="secondary" onClick={() => startEdit(inv)}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color={inv.active ? "warning" : "success"}
                      disabled={togglePending}
                      onClick={() => toggleActiveMutation.mutate(inv)}
                    >
                      {inv.active ? "Revoke" : "Reactivate"}
                    </Button>
                  </Box>
                ) : null}
              </Box>

              {expandedCode === inv.code ? (
                <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                  {deliveriesBusy && !deliveries[inv.code] ? (
                    <CircularProgress size={18} />
                  ) : (deliveries[inv.code]?.length ?? 0) === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      No delivery attempts yet.
                    </Typography>
                  ) : (
                    (deliveries[inv.code] ?? []).map((d) => (
                      <Box key={d.id} sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                        <Typography variant="body2">{d.email}</Typography>
                        <Typography variant="body2" color={d.status === "failed" || d.status === "bounced" ? "error" : "text.secondary"}>
                          {d.status}
                          {d.error ? ` — ${d.error}` : ""}
                        </Typography>
                      </Box>
                    ))
                  )}
                  {deliveriesError ? <Alert severity="error">{deliveriesError}</Alert> : null}
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <TextField
                      size="small"
                      type="email"
                      placeholder="Add another email…"
                      value={resendEmail[inv.code] ?? ""}
                      onChange={(e) => setResendEmail((m) => ({ ...m, [inv.code]: e.target.value }))}
                      fullWidth
                    />
                    <Button size="small" variant="contained" color="secondary" disabled={deliveriesBusy} onClick={() => resend(inv)}>
                      Resend
                    </Button>
                  </Stack>
                </Box>
              ) : null}

              {editingCode === inv.code ? (
                <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Edit invite</Typography>
                  <Typography variant="body2" color="warning.dark" sx={{ mb: 1 }}>
                    Changes apply only to future redemptions.
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5}>
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Max uses"
                        value={editMaxUses}
                        onChange={(e) => setEditMaxUses(e.target.value)}
                        fullWidth
                      />
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Days until expiry"
                        value={editDays}
                        onChange={(e) => setEditDays(e.target.value)}
                        fullWidth
                      />
                    </Stack>
                    <TextField size="small" placeholder="Note" value={editNote} onChange={(e) => setEditNote(e.target.value)} />
                    <TextField
                      size="small"
                      placeholder="Welcome message"
                      value={editMessage}
                      onChange={(e) => setEditMessage(e.target.value)}
                    />
                    <GrantPicker tree={tree} baseUrl={contentBaseUrl} selected={editGrants} onToggle={toggleEditGrant} error={treeError} />
                    {saveEditMutation.isError ? <Alert severity="error">{(saveEditMutation.error as Error).message}</Alert> : null}
                    <Stack direction="row" spacing={1.5}>
                      <Button size="small" variant="contained" disabled={saveEditMutation.isPending} onClick={() => saveEditMutation.mutate(inv)}>
                        Save
                      </Button>
                      <Button size="small" variant="outlined" color="secondary" onClick={() => setEditingCode(null)}>
                        Cancel
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              ) : null}
            </MainCard>
            );
          })}
          </Stack>
        </>
      )}

      <Link component={RouterLink} to="/invite-attempts" sx={{ display: "inline-block", mt: 2 }}>
        Acceptance history →
      </Link>
    </MainCard>
  );

  return (
    <TabbedPage
      defaultTab="invites"
      tabs={[
        { id: "invites", label: "Invites", content: invitesTabContent },
        { id: "create", label: "Create invite", content: createTabContent },
      ]}
    />
  );
}
